/**

* Waits for the gtag script to be available.
 * @param {number} [interval=100] - The interval (in milliseconds) between attempts to check for the gtag script.
 * @param {number} [maxAttempts=10] - The maximum number of attempts to check for the gtag script.
 * @returns {Promise<void>} - A promise that resolves when the gtag script becomes available, or rejects if the maximum attempts are reached.
 */
export function waitForGtag(interval = 100, maxAttempts = 10) {
	return new Promise((resolve, reject) => {
		const checkGtag = attempt => {
			if (typeof gtag !== 'undefined') {
				// gtag is available, resolve the promise
				resolve()
			} else {
				// gtag is not yet available
				if (attempt < maxAttempts) {
					// Wait for a short interval and try again
					setTimeout(() => checkGtag(attempt + 1), interval)
				} else {
					// Maximum attempts reached, reject the promise
					reject(new Error('Maximum attempts reached. Unable to load gtag.'))
				}
			}
		}

		// Start checking for gtag availability
		checkGtag(1)
	})
}

/**

* Waits for heap to be available.
 * @param {number} [interval=100] - The interval (in milliseconds) between attempts to check for heap.
 * @param {number} [maxAttempts=10] - The maximum number of attempts to check for heap.
 * @returns {Promise<void>} - A promise that resolves when heap becomes available, or rejects if the maximum attempts are reached.
 */
export function waitForHeap(interval = 100, maxAttempts = 10) {
	return new Promise((resolve, reject) => {
		const checkHeap = attempt => {
			if (typeof heap !== 'undefined') {
				resolve()
			} else {
				if (attempt < maxAttempts) {
					setTimeout(() => checkHeap(attempt + 1), interval)
				} else {
					// Maximum attempts reached, reject the promise
					reject(new Error('Maximum attempts reached. Unable to load heap.'))
				}
			}
		}

		checkHeap(1)
	})
}

/**
 * Checks if an element is visible in the DOM hierarchy, considering parent elements with display: none.
 * @param {HTMLElement} element - The element to check for visibility.
 * @returns {boolean} True if the element is visible, false otherwise.
 * @example
 * // Example usage:
 * const element = document.querySelector('.my-element');
 * if (isElementVisible(element)) {
 *   console.log('Element is visible');
 * } else {
 *   console.log('Element is not visible');
 * }
 */
function isElementVisible(element) {
	// Check if element itself is hidden by display: none
	const computedStyle = window.getComputedStyle(element)
	if (computedStyle.display === 'none') {
		return false
	}

	// Check if any parent element is hidden by display: none
	let parent = element.parentElement
	while (parent) {
		const parentComputedStyle = window.getComputedStyle(parent)
		if (parentComputedStyle.display === 'none') {
			return false
		}
		parent = parent.parentElement
	}

	return true
}

/**
 * Finds visible elements matching the provided CSS selector that don't already have
 * the specified attribute set.
 * @param {string} selector - The CSS selector for the elements to search for.
 * @param {string} attribute - The attribute to check for absence.
 * @returns {Array.<Element>} An array containing only the visible elements in the DOM
 * hierarchy matching the selector and without the specified attribute set.
 * @example
 * // Example usage:
 * const enrollNowCtas = findVisibleElementsWithoutAttribute('[data-cta-type="enroll-now"]', 'element_index')
 * console.log(enrollNowCtas) // Array of visible elements with data-cta-type="enroll-now" and no 'element_index' attribute
 *
 * const visibleImages = findVisibleElementsWithoutAttribute('img', 'element_index')
 * console.log(visibleImages) // Array of visible images with no 'element_index' attribute
 */
export function findVisibleElementsWithoutAttribute(selector) {
	// Select all elements matching the provided selector
	const elements = document.querySelectorAll(selector)
	const attribute = 'element_index'
	// Filter out the visible elements without the specified attribute set
	const visibleElements = Array.from(elements).filter(function(element) {
		// Return false if the specified attribute is already set
		if (element.hasAttribute(attribute)) {
			return false
		}
		return isElementVisible(element)
	})
	// console.log(`Visible elements for ${selector} without attribute '${attribute}':`, visibleElements)
	return visibleElements
}

/**
 * Sets the 'element_index' attribute for visible elements matching the provided CSS selector.
 * Used to track the position of elements on the page.
 * @param {string} selector - The CSS selector for the elements to set the attribute for.
 * @example
 * // Example usage:
 * setElementIndexAttribute('[data-cta-type="enroll-now"]')
 * setElementIndexAttribute('[data-cta-type="plan-details"]')
 */
export function setElementIndexAttribute(selector) {
	const elements = findVisibleElementsWithoutAttribute(selector)
	if (elements.length === 0) {
		return
	}
	elements.forEach((element, index) => {
		element.setAttribute('element_index', index + 1)
	})
}
